import { z } from 'zod';

/**
 * What makes a password acceptable.
 *
 * The rules follow NIST SP 800-63B, which is the guidance the rest of the
 * industry has converged on and which is deliberately *not* the older
 * "one uppercase, one digit, one symbol" checklist. Composition rules produce
 * `Password1!` and nothing else: they narrow the search space for an attacker
 * while making the result harder for a person to remember. What actually helps
 * is a floor on length plus a refusal of the passwords everybody already
 * tries.
 *
 * So the policy is three things:
 *
 *   1. A length floor and ceiling. The ceiling is not about strength — it
 *      bounds the work scrypt does on a request.
 *   2. A blocklist of the passwords that appear at the top of every breach
 *      corpus, normalised so that `Password123!` is caught as `password`.
 *   3. A refusal of patterns that are technically long but carry no entropy:
 *      one character repeated, or a straight run of a keyboard row.
 *
 * Sign-in deliberately does not apply any of this. A policy is for *choosing*
 * a password, never for checking one: tightening the rules must not lock out
 * somebody who registered under the old ones.
 */

/** Long enough to matter, short enough that nobody writes it on a sticky note. */
export const PASSWORD_MIN_LENGTH = 10;

/** Bounds the scrypt cost of a single request; not a security boundary. */
export const PASSWORD_MAX_LENGTH = 200;

/**
 * The passwords worth refusing outright.
 *
 * Sourced from the top of the public breach corpora (rockyou, the Have I Been
 * Pwned top lists) plus the handful of words that are specific to this product
 * and its market — a Spanish-speaking user is as likely to type `contraseña`
 * or `argentina` as `password`, and an attacker enumerating this deployment
 * would try `arriba.digital` first.
 *
 * Not exhaustive, and not meant to be: this is the cheap, zero-dependency
 * layer. A deployment that wants the full corpus should check the candidate
 * against a breached-password service at sign-up instead.
 */
const COMMON_PASSWORDS: readonly string[] = [
  '123456', '123456789', '12345678', '1234567', '12345', '1234567890', '111111', '000000',
  '222222', '333333', '444444', '555555', '666666', '777777', '888888', '999999', '121212',
  '123123', '123321', '112233', '102030', '010203', '987654321', '654321', '12121212',
  'qwerty', 'qwertyuiop', 'qwerty123', 'qwertyui', 'asdfgh', 'asdfghjkl', 'zxcvbn', 'zxcvbnm',
  '1q2w3e4r', '1qaz2wsx', 'qazwsx', 'qwe123', 'abc123', 'abcd1234', 'a1b2c3', 'aa123456',
  'password', 'password1', 'passw0rd', 'passwd', 'contraseña', 'clave', 'clave123',
  'letmein', 'welcome', 'welcome1', 'admin', 'administrator', 'root', 'toor', 'guest', 'default',
  'iloveyou', 'monkey', 'dragon', 'master', 'sunshine', 'princess', 'football', 'baseball',
  'shadow', 'superman', 'batman', 'michael', 'jennifer', 'jordan', 'harley', 'ranger', 'buster',
  'soccer', 'hockey', 'killer', 'george', 'sexy', 'andrew', 'charlie', 'thomas', 'robert',
  'trustno1', 'whatever', 'starwars', 'computer', 'internet', 'samsung', 'google', 'facebook',
  'mercedes', 'ferrari', 'porsche', 'corvette', 'mustang', 'harley1', 'yamaha', 'honda',
  'matrix', 'freedom', 'secret', 'flower', 'hello', 'hello123', 'hunter', 'hunter2', 'ginger',
  'cookie', 'banana', 'orange', 'purple', 'silver', 'golden', 'diamond', 'phoenix', 'tigger',
  'chocolate', 'butterfly', 'sunflower', 'watermelon', 'pineapple', 'strawberry', 'blueberry',
  'qwerty12', 'zaq12wsx', 'qwertz', 'azerty', 'liverpool', 'chelsea', 'arsenal', 'barcelona',
  'realmadrid', 'riverplate', 'bocajuniors', 'maradona', 'messi', 'futbol',
  'argentina', 'buenosaires', 'rosario', 'cordoba', 'mendoza', 'tucuman',
  'chile', 'santiago', 'valparaiso', 'concepcion', 'antofagasta',
  'latinoamerica', 'colombia', 'mexico', 'peru', 'uruguay', 'paraguay',
  'bolivia', 'brasil', 'brazil', 'espana', 'madrid', 'barcelona1',
  'arriba.digital', 'arriba', 'digital', 'retailapp', 'retail', 'inventario', 'facturacion', 'facturación', 'empresa', 'negocio',
  'comercio', 'almacen', 'almacén', 'producto', 'sistema', 'usuario', 'gerente', 'dueno',
  'dueño', 'jefe', 'ventas', 'stock', 'deposito', 'depósito',
  'aaaaaaaa', 'aaaaaaaaaa', '12341234', '1234abcd', 'abcd123456', 'password123', 'password12',
  'admin123', 'admin1234', 'root1234', 'test1234', 'demo1234', 'changeme', 'changeme1',
];

/**
 * Keyboard rows, as they are actually typed. A run from one of these is long
 * but has no entropy at all.
 */
const KEYBOARD_ROWS: readonly string[] = [
  'qwertyuiop',
  'asdfghjkl',
  'zxcvbnm',
  '1234567890',
];

/**
 * Fold a string to the form the comparisons use: no diacritics, no case.
 *
 * `Ríos` and `rios` are the same word to the person who typed them, and the
 * same word to anyone guessing. Comparing the raw strings would let
 * `contraseña` through while blocking `contrasena`, which is backwards — the
 * accented spelling is the one a Spanish speaker reaches for first.
 */
function fold(value: string): string {
  return value
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLowerCase();
}

/** The list, folded once at load, for the exact-match check. */
const BLOCKED = new Set(COMMON_PASSWORDS.map(fold));

/** Why a password was refused. `null` means it was accepted. */
export type PasswordProblem =
  | 'passwordTooShort'
  | 'passwordTooLong'
  | 'passwordCommon'
  | 'passwordRepeated'
  | 'passwordSequential'
  | 'passwordContainsEmail'
  | 'passwordContainsName';

/**
 * Strip the decoration people add to a blocked word.
 *
 * `Password123!` is the same password as `password` with a hat on, and every
 * cracking tool knows it: the mangling rules try the dictionary word with
 * digits and symbols appended first. Matching on the bare stem is what turns a
 * list of two hundred words into a list of several thousand effective ones.
 *
 * Only trailing decoration is removed. Leading characters are left alone,
 * because a prefix changes the word.
 */
function stemOf(candidate: string): string {
  return fold(candidate).replace(/[^a-z0-9]+$/u, '').replace(/\d+$/, '');
}

/** True when the string is one character repeated, e.g. `aaaaaaaaaaaa`. */
function isRepeated(candidate: string): boolean {
  return candidate.length >= 6 && new Set(candidate).size === 1;
}

/** True when the string is a straight run along a keyboard row or the digits. */
function isSequential(candidate: string): boolean {
  const lowered = candidate.toLowerCase();
  if (lowered.length < 6) {
    return false;
  }

  for (const row of KEYBOARD_ROWS) {
    for (const direction of [row, [...row].reverse().join('')]) {
      for (let start = 0; start + lowered.length <= direction.length; start += 1) {
        if (direction.slice(start, start + lowered.length) === lowered) {
          return true;
        }
      }
    }
  }

  return false;
}

/**
 * The parts of a password that are long enough to be worth looking for inside
 * it. A two-character local part would match almost anything.
 */
function contextWords(context?: { email?: string; fullName?: string }): string[] {
  if (!context) {
    return [];
  }

  const words: string[] = [];
  const localPart = fold(context.email?.split('@')[0] ?? '');
  if (localPart.length >= 4) {
    words.push(localPart);
  }
  if (context.fullName) {
    for (const word of fold(context.fullName).split(/[^\p{L}\p{N}]+/u)) {
      if (word.length >= 4) {
        words.push(word);
      }
    }
  }

  return words;
}

/**
 * Judge a candidate password.
 *
 * Pure and dependency-free so the browser can run exactly the same check the
 * API does: a form that accepts a password the server will reject is worse
 * than no client-side check at all.
 */
export function inspectPassword(
  password: string,
  context?: { email?: string; fullName?: string },
): PasswordProblem | null {
  // Normalised the same way `hashPassword` normalises before hashing, so the
  // policy and the hash agree about what the password actually is.
  const candidate = password.normalize('NFKC');

  if (candidate.length < PASSWORD_MIN_LENGTH) {
    return 'passwordTooShort';
  }
  if (candidate.length > PASSWORD_MAX_LENGTH) {
    return 'passwordTooLong';
  }

  const lowered = fold(candidate);
  if (BLOCKED.has(lowered) || BLOCKED.has(stemOf(lowered))) {
    return 'passwordCommon';
  }

  if (isRepeated(candidate)) {
    return 'passwordRepeated';
  }
  if (isSequential(candidate)) {
    return 'passwordSequential';
  }

  const words = contextWords(context);
  const localPart = fold(context?.email?.split('@')[0] ?? '');
  for (const word of words) {
    if (lowered.includes(word)) {
      return word === localPart ? 'passwordContainsEmail' : 'passwordContainsName';
    }
  }

  return null;
}

/** The translation key the interface should show for a problem. */
export function passwordProblemKey(problem: PasswordProblem): string {
  return `validation.${problem}`;
}

/**
 * The schema every password-setting form shares.
 *
 * Carries the context-free rules only. A schema cannot see the address the
 * password belongs to, so the "does it contain your own e-mail" check is
 * attached where the context exists — see `RegisterCompanySchema`.
 */
export const PasswordSchema = z
  .string()
  .min(PASSWORD_MIN_LENGTH, { message: passwordProblemKey('passwordTooShort') })
  .max(PASSWORD_MAX_LENGTH, { message: passwordProblemKey('passwordTooLong') })
  .superRefine((value, ctx) => {
    // Length is already reported by the checks above; repeating it here would
    // show the same complaint twice.
    if (value.length < PASSWORD_MIN_LENGTH || value.length > PASSWORD_MAX_LENGTH) {
      return;
    }

    const problem = inspectPassword(value);
    if (problem) {
      ctx.addIssue({ code: 'custom', message: passwordProblemKey(problem) });
    }
  });
